import { ethers } from "ethers";
import { approveToken, getProvider, getTokenAllowance, getWeb3 } from ".";
import MafiaProfileTicketAbi from "../../abis/MafiaProfile.json";
import config from "../../config/config";
import { PROFILE_PRICE_IN_MAFIA, WalletErrorMsg } from "../../constants/const";
import { TravelInfo } from "../../types/Contract/Profile/TravelInfo";
import { SwapToken } from "../../types/Contract/SwapToken";
import { delay } from "../../utils/utils";

export const getProfileContract = (provider = false) => {
  const web3 = provider ? getProvider() : getWeb3();
  const contractAddress = config.profileAddress;
  const ProfileContract = new web3.eth.Contract(
    MafiaProfileTicketAbi,
    contractAddress
  );
  return ProfileContract;
};

export const createProfile = async (
  name: string,
  swapTokenId: number,
  tokenAddress: string,
  tokenAmount: number,
  account: string
) => {
  const ProfileContract = getProfileContract(true);
  const web3 = getProvider();

  if (tokenAddress === ethers.constants.AddressZero) {
    const nativeAmount = web3.utils.toWei(tokenAmount.toFixed(18), "ether");

    await ProfileContract.methods.createProfile(name, swapTokenId).send({
      value: nativeAmount,
      from: account,
    });
  } else {
    const tokenAllowance = await getTokenAllowance(
      tokenAddress,
      account,
      config.profileAddress
    );
    try {
      if (Number(tokenAllowance) < tokenAmount) {
        await approveToken(
          tokenAddress,
          tokenAmount,
          config.profileAddress,
          account
        );

        await delay(config.blockTime);
      }
    } catch (error: any) {
      if (error.message === WalletErrorMsg.Indexing) {
        await delay(config.blockTime);
      } else if (error.message === WalletErrorMsg.Rejected) {
        throw error;
      } else {
        throw error;
      }
    }

    await ProfileContract.methods
      .createProfile(name, swapTokenId)
      .send({ from: account });
  }
};

export const approveTicketContract = async (account: string) => {
  const profilePrice = PROFILE_PRICE_IN_MAFIA;
  const tokenAddress = config.mafiaAddress;
  const contractAddress = config.profileAddress;

  const allowance = await getTokenAllowance(
    tokenAddress,
    account,
    contractAddress
  );

  if (Number(allowance) < profilePrice) {
    await approveToken(tokenAddress, profilePrice, contractAddress, account);
  }
};

export const userTicketId = async (account: string) => {
  const ProfileContract = getProfileContract(false);

  const ticketId = await ProfileContract.methods.userTicketId(account).call();

  return ticketId;
};

export const boughtTicket = async (account: string) => {
  const ProfileContract = getProfileContract(false);

  const bought: boolean = await ProfileContract.methods
    .boughtTicket(account)
    .call();

  return bought;
};

export const getSwapTokens = async () => {
  const ProfileContract = getProfileContract();

  let swapTokenInfo: [SwapToken[], number[]];
  swapTokenInfo = await ProfileContract.methods.getSwapTokens().call();

  return swapTokenInfo;
};

export const startTravel = async (
  destinationCity: number,
  travelType: number,
  itemId: number,
  account: string
) => {
  const ProfileContract = getProfileContract(true);

  const tx = await ProfileContract.methods
    .startTravel(destinationCity, travelType, itemId)
    .send({ from: account });

  return tx;
};

export const finishTravel = async (account: string) => {
  const ProfileContract = getProfileContract(true);

  const tx = await ProfileContract.methods
    .finishTravel()
    .send({ from: account });

  return tx;
};

export const travel = async (
  destinationCity: number,
  travelType: number,
  itemId: number,
  account: string
) => {
  const ProfileContract = getProfileContract(true);

  const tx = await ProfileContract.methods
    .travel(destinationCity, travelType, itemId)
    .send({ from: account });

  return tx;
};

export const getUserTravelInfo = async (
  account: string,
  signature: string,
  signMsg: string
) => {
  const ProfileContract = getProfileContract();

  const travelInfo: TravelInfo = await ProfileContract.methods
    .getUserTravelInfo(account, JSON.stringify(signMsg), signature)
    .call();

  return {
    travelType: Number(travelInfo.travelType),
    travelUntil: Number(travelInfo.travelUntil),
    itemId: Number(travelInfo.itemId),
  } as TravelInfo;
};
